import { useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { FunctionReturnType } from "convex/server";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Plus, Trash2 } from "lucide-react";
import { FormActions, PageHeader } from "@/components/layout";
import { StatusBadge } from "@/components/status-badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multi-select";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useCanEdit } from "@/lib/capabilities";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";

export type AiSetup = FunctionReturnType<typeof api.takeoffPilot.aiWorkflow.get>;
type Details = NonNullable<FunctionReturnType<typeof api.takeoffPilot.jobs.getWorkspaceDetails>>;
type Selection = AiSetup["selections"][number];
type MeasurementBasis = Selection["measurementBasis"];

const TRAIL = [{ label: "TakeoffPilot", href: "/takeoffPilot" }];

/** True while the job must finish AI setup before its drawing viewer opens. */
export function isAiSetupPending(details: Details | null | undefined, setup: AiSetup | undefined) {
  return Boolean(details?.job.aiTakeoffRequested && details.job.status !== "failed" && setup &&
    (!setup.started || (setup.status === "needs_attention" && setup.canReopen)));
}

const MEASUREMENT_OPTIONS = [
  { value: "count", label: "Count" },
  { value: "LF", label: "Length (LF)" },
  { value: "SQFT", label: "Area (SF)" },
] as const;

/**
 * The user picks how each drawing page is measured until AI infers it per item. When inference
 * ships, drop this field; the rest of the setup does not depend on it.
 */
function MeasurementTypeField({ label, value, disabled, onChange }: {
  label: string;
  value: MeasurementBasis;
  disabled: boolean;
  onChange: (value: MeasurementBasis) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium" aria-hidden="true">Measure As</p>
      <SegmentedControl aria-label={label} value={value} disabled={disabled} onValueChange={onChange} options={MEASUREMENT_OPTIONS} />
      {value !== "count" && <p className="text-sm text-muted-foreground">Uses the page scale. You may be asked to confirm it during review.</p>}
    </div>
  );
}

export function AiTakeoffSetup({ details, initial }: { details: Details; initial: AiSetup }) {
  const navigate = useAppNavigate();
  const canEdit = useCanEdit();
  const save = useMutation(api.takeoffPilot.aiWorkflow.save);
  const reopen = useMutation(api.takeoffPilot.aiWorkflow.reopenSetup);
  const [selections, setSelections] = useState<Selection[]>(initial.selections);
  const [includeExisting, setIncludeExisting] = useState(initial.includeExisting);
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState("All changes saved");
  const [error, setError] = useState<string | null>(null);
  const version = useRef(initial.version);
  const writes = useRef<Promise<unknown>>(Promise.resolve());
  const started = useRef(false);
  const initialRender = useRef(true);
  const jobId = details.job._id;
  const prepared = !["extracting_schedule", "copying_revision", "failed"].includes(details.job.status);
  const sections = details.tables.filter(table => !table.isCustom).map(table => ({
    id: table._id, label: table.name, description: `Schedule page ${table.pageNumber}`,
    options: details.tags.filter(tag => tag.sourceTableId === table._id && !tag.isCustom).map(tag => ({ value: tag._id, label: tag.rawValue })),
  }));
  const ready = selections.length > 0 && selections.every(selection => selection.tagIds.length > 0);

  function persist(start: boolean) {
    const snapshot = { selections, includeExisting };
    const next = writes.current.catch(() => undefined).then(async () => {
      if (started.current) return;
      setSaveState("Saving…");
      version.current = await save({ jobId, version: version.current, ...snapshot, start });
      if (start) started.current = true;
      setSaveState("All changes saved");
      setError(null);
    });
    writes.current = next;
    return next;
  }

  useEffect(() => {
    if (initialRender.current) { initialRender.current = false; return; }
    if (!canEdit || busy || initial.started) return;
    setSaveState("Unsaved changes");
    const timer = window.setTimeout(() => {
      void persist(false).catch(error => {
        setSaveState("Changes not saved");
        setError(getUserFacingErrorMessage(error, "Unable to save AI setup. Try saving again."));
      });
    }, 600);
    return () => window.clearTimeout(timer);
    // Persist the local draft after edits; reactive query responses must not replace it.
  }, [selections, includeExisting, canEdit, busy]);

  async function leave(start: boolean) {
    setBusy(true);
    try {
      await persist(start);
      if (start) navigate({ to: "/takeoffPilot/$jobId", params: { jobId }, title: details.job.name, replace: true });
      else navigate({ to: "/takeoffPilot", title: "TakeoffPilot" });
    } catch (error) {
      setError(getUserFacingErrorMessage(error, "Unable to start AI takeoff. Your saved setup is preserved."));
    } finally { setBusy(false); }
  }

  function update(index: number, patch: Partial<Selection>) {
    setSelections(previous => previous.map((selection, current) => current === index ? { ...selection, ...patch } : selection));
  }

  if (initial.started) return (
    <div className="space-y-4">
      <PageHeader title={details.job.name} trail={TRAIL} meta={<StatusBadge status="error" label="Needs Attention" />} />
      <Alert variant="warning" className="max-w-3xl">
        <AlertTitle>AI Takeoff Could Not Start</AlertTitle>
        <AlertDescription>Preparation stopped before any measuring began. Your selected items and pages are saved.</AlertDescription>
      </Alert>
      {initial.preparationErrors.length > 0 && <p role="alert" className="text-sm text-destructive">{Array.from(new Set(initial.preparationErrors)).join(" ")}</p>}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button disabled={!canEdit || busy} onClick={() => {
          setBusy(true);
          void reopen({ jobId, version: initial.version }).catch(error => setError(getUserFacingErrorMessage(error, "Unable to reopen AI setup"))).finally(() => setBusy(false));
        }}>Edit Setup</Button>
        <Button variant="outline" onClick={() => navigate({ to: "/takeoffPilot", title: "TakeoffPilot" })}>Back To Jobs</Button>
      </div>
    </div>
  );

  const editable = canEdit && !busy;
  return (
    <div className="space-y-4">
      <PageHeader title={details.job.name} trail={TRAIL} meta={<StatusBadge status="pending" label="AI Setup" showSpinner={false} />} />
      <div className="max-w-3xl space-y-4">
        {!prepared && (
          <Alert variant={details.job.status === "failed" ? "destructive" : "default"}>
            <AlertTitle>{details.job.status === "failed" ? "Document Preparation Failed" : "Preparing Your Schedule"}</AlertTitle>
            <AlertDescription>
              {details.job.status === "failed"
                ? "Resolve the job's document issue before starting AI takeoff."
                : "Schedule items will be available to choose once preparation finishes."}
            </AlertDescription>
          </Alert>
        )}
        {!canEdit && <Alert><AlertDescription>A project member with edit access can choose items and start the takeoff.</AlertDescription></Alert>}

        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">What Should AI Measure?</CardTitle>
            <CardDescription>Pick the schedule items to find on each drawing page. Choose one primary page per item so nothing is counted twice.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {selections.length === 0 && <p className="text-sm text-muted-foreground">No drawing pages yet. Add one to choose items.</p>}
            {selections.map((selection, index) => {
              const chosenElsewhere = new Set(selections.filter((_, current) => current !== index).flatMap(group => group.tagIds));
              const available = sections.map(section => ({ ...section, options: section.options.filter(option => !chosenElsewhere.has(option.value)) }));
              const n = index + 1;
              return (
                <section key={index} aria-label={`Drawing page ${n}`} className="space-y-4 rounded-lg border-2 bg-muted/20 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold">Drawing Page {n}</h3>
                    <IconButton icon={Trash2} label={`Remove drawing page ${n}`} size="sm" disabled={!editable} onClick={() => setSelections(previous => previous.filter((_, current) => current !== index))} />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_6rem]">
                    <div className="space-y-2">
                      <Label htmlFor={`ai-drawing-${index}`}>Drawing</Label>
                      <Select value={selection.planDocumentId} disabled={!editable} onValueChange={value => update(index, { planDocumentId: value as Selection["planDocumentId"] })}>
                        <SelectTrigger id={`ai-drawing-${index}`} className="w-full"><SelectValue /></SelectTrigger>
                        <SelectContent>{details.planDocuments.map(plan => <SelectItem key={plan._id} value={plan._id}>{plan.displayName}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`ai-page-${index}`}>Page</Label>
                      <Input id={`ai-page-${index}`} type="number" min={1} step={1} value={selection.pageNumber} disabled={!editable} onChange={event => update(index, { pageNumber: Math.max(1, Math.floor(Number(event.target.value) || 1)) })} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Items</Label>
                    <MultiSelect sections={available} selected={selection.tagIds} triggerAriaLabel={`Items for drawing page ${n}`} triggerLabel="Items" placeholder="Choose schedule items" triggerWidth="w-full" dropdownWidth="w-[min(40rem,calc(100vw-3rem))]" columns="auto-fit" disabled={!editable || !prepared}
                      onSelect={value => update(index, { tagIds: [...selection.tagIds, value as Id<"takeoffPilotExtractedTags">] })}
                      onRemove={value => update(index, { tagIds: selection.tagIds.filter(id => id !== value) })}
                      onSelectAll={id => update(index, { tagIds: Array.from(new Set([...selection.tagIds, ...(available.find(section => section.id === id)?.options.map(option => option.value) ?? [])])) })}
                      onDeselectAll={id => { const removed = new Set(available.find(section => section.id === id)?.options.map(option => option.value)); update(index, { tagIds: selection.tagIds.filter(tagId => !removed.has(tagId)) }); }} />
                  </div>
                  <MeasurementTypeField label={`Measure drawing page ${n} as`} value={selection.measurementBasis} disabled={!editable} onChange={measurementBasis => update(index, { measurementBasis })} />
                </section>
              );
            })}
            <Button variant="outline" size="sm" disabled={!editable || !prepared || !details.planDocuments.length} onClick={() => setSelections(previous => [...previous, { planDocumentId: details.planDocuments[0]._id, pageNumber: 1, tagIds: [], measurementBasis: "count" }])}>
              <Plus aria-hidden="true" />Add Drawing Page
            </Button>
          </CardContent>
        </Card>

        <div className="flex items-start gap-3">
          <Switch id="ai-include-existing" checked={includeExisting} onCheckedChange={setIncludeExisting} disabled={!editable} />
          <div className="space-y-1">
            <Label htmlFor="ai-include-existing">Include Existing Items</Label>
            <p className="text-sm text-muted-foreground">Also measure existing items shown as remaining in place.</p>
          </div>
        </div>

        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <FormActions
          cancelLabel={canEdit ? "Save Draft" : "Back To Jobs"}
          onCancel={() => canEdit ? void leave(false) : navigate({ to: "/takeoffPilot", title: "TakeoffPilot" })}
          submitLabel="Start AI Takeoff"
          submitting={busy}
          submitDisabled={!canEdit || !prepared || !ready}
          onSubmit={() => void leave(true)}
          className="border-t pt-4"
        >
          {canEdit && <span role="status">{saveState}</span>}
        </FormActions>
        <p className="text-sm text-muted-foreground">You can leave while AI works. Results appear for review in the drawing viewer.</p>
      </div>
    </div>
  );
}
